import React, { useState } from 'react';
import { Code, Copy, Trash2 } from 'lucide-react';

const CodeSnippetGeneratorTool = () => {
  const [config, setConfig] = useState({
    language: 'javascript',
    type: 'function',
    name: '',
    params: '',
    description: ''
  });
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);

  const templates = {
    javascript: {
      function: (c) => `/**
 * ${c.description || 'Description of the function'}
 * @param {${c.params ? c.params.split(',').map(p => `any} ${p.trim()}`).join('\n * @param {') : 'void'}}
 * @returns {any}
 */
function ${c.name || 'myFunction'}(${c.params || ''}) {
  // TODO: Implement function
  
}`,
      arrowFunction: (c) => `/**
 * ${c.description || 'Description of the function'}
 */
const ${c.name || 'myFunction'} = (${c.params || ''}) => {
  // TODO: Implement function
  
};`,
      class: (c) => `/**
 * ${c.description || 'Class description'}
 */
class ${c.name || 'MyClass'} {
  constructor(${c.params || ''}) {
    // Initialize properties
  }
  
  // Methods
}`,
      asyncFunction: (c) => `/**
 * ${c.description || 'Async function description'}
 */
async function ${c.name || 'myAsyncFunction'}(${c.params || ''}) {
  try {
    // TODO: Implement async logic
    
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
}`,
      reactComponent: (c) => `import React from 'react';

/**
 * ${c.description || 'React component description'}
 */
const ${c.name || 'MyComponent'} = ({ ${c.params || ''} }) => {
  return (
    <div>
      {/* Component content */}
    </div>
  );
};

export default ${c.name || 'MyComponent'};`,
      expressRoute: (c) => `/**
 * ${c.description || 'Express route handler'}
 */
router.get('/${c.name || 'route'}', async (req, res) => {
  try {
    // TODO: Implement route logic
    
    res.json({ success: true });
  } catch (error) {
    console.error('Error:', error);
    res.status(500).json({ error: 'Internal server error' });
  }
});`,
    },
    python: {
      function: (c) => `def ${c.name || 'my_function'}(${c.params || ''}):
    """
    ${c.description || 'Description of the function'}
    
    Args:
        ${c.params ? c.params.split(',').map(p => `${p.trim()}: Description`).join('\n        ') : 'None'}
    
    Returns:
        Description of return value
    """
    # TODO: Implement function
    pass`,
      class: (c) => `class ${c.name || 'MyClass'}:
    """
    ${c.description || 'Class description'}
    """
    
    def __init__(self${c.params ? ', ' + c.params : ''}):
        """Initialize the class."""
        ${c.params ? c.params.split(',').map(p => `self.${p.trim()} = ${p.trim()}`).join('\n        ') : 'pass'}
    
    def __str__(self):
        return f"${c.name || 'MyClass'}()"`,
      asyncFunction: (c) => `async def ${c.name || 'my_async_function'}(${c.params || ''}):
    """
    ${c.description || 'Async function description'}
    """
    try:
        # TODO: Implement async logic
        pass
    except Exception as e:
        print(f"Error: {e}")
        raise`,
      fastApiRoute: (c) => `@app.get("/${c.name || 'route'}")
async def ${c.name || 'get_route'}(${c.params || ''}):
    """
    ${c.description || 'FastAPI route description'}
    """
    try:
        # TODO: Implement route logic
        return {"success": True}
    except Exception as e:
        raise HTTPException(status_code=500, detail=str(e))`,
    },
    typescript: {
      function: (c) => `/**
 * ${c.description || 'Description of the function'}
 */
function ${c.name || 'myFunction'}(${c.params ? c.params.split(',').map(p => `${p.trim()}: any`).join(', ') : ''}): any {
  // TODO: Implement function
  
}`,
      interface: (c) => `/**
 * ${c.description || 'Interface description'}
 */
interface ${c.name || 'MyInterface'} {
  ${c.params ? c.params.split(',').map(p => `${p.trim()}: any;`).join('\n  ') : '// Add properties'}
}`,
      typeAlias: (c) => `/**
 * ${c.description || 'Type description'}
 */
type ${c.name || 'MyType'} = {
  ${c.params ? c.params.split(',').map(p => `${p.trim()}: any;`).join('\n  ') : '// Add properties'}
};`,
      reactComponent: (c) => `import React from 'react';

interface ${c.name || 'MyComponent'}Props {
  ${c.params ? c.params.split(',').map(p => `${p.trim()}: any;`).join('\n  ') : '// Add props'}
}

/**
 * ${c.description || 'React component description'}
 */
const ${c.name || 'MyComponent'}: React.FC<${c.name || 'MyComponent'}Props> = ({ ${c.params || ''} }) => {
  return (
    <div>
      {/* Component content */}
    </div>
  );
};

export default ${c.name || 'MyComponent'};`,
    }
  };

  const generateSnippet = () => {
    const langTemplates = templates[config.language];
    if (!langTemplates || !langTemplates[config.type]) {
      setOutput('Template not available');
      return;
    }
    setOutput(langTemplates[config.type](config));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const typeOptions = {
    javascript: ['function', 'arrowFunction', 'class', 'asyncFunction', 'reactComponent', 'expressRoute'],
    python: ['function', 'class', 'asyncFunction', 'fastApiRoute'],
    typescript: ['function', 'interface', 'typeAlias', 'reactComponent']
  };

  const typeLabels = {
    function: 'Function',
    arrowFunction: 'Arrow Function',
    class: 'Class',
    asyncFunction: 'Async Function',
    reactComponent: 'React Component',
    expressRoute: 'Express Route',
    fastApiRoute: 'FastAPI Route',
    interface: 'Interface',
    typeAlias: 'Type Alias'
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Language</label>
          <select
            value={config.language}
            onChange={(e) => setConfig({ ...config, language: e.target.value, type: typeOptions[e.target.value][0] })}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            <option value="javascript">JavaScript</option>
            <option value="typescript">TypeScript</option>
            <option value="python">Python</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Snippet Type</label>
          <select
            value={config.type}
            onChange={(e) => setConfig({ ...config, type: e.target.value })}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            {typeOptions[config.language].map(type => (
              <option key={type} value={type}>{typeLabels[type]}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
          <input
            type="text"
            value={config.name}
            onChange={(e) => setConfig({ ...config, name: e.target.value })}
            placeholder="myFunction"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Parameters (comma-separated)</label>
          <input
            type="text"
            value={config.params}
            onChange={(e) => setConfig({ ...config, params: e.target.value })}
            placeholder="param1, param2"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
        <input
          type="text"
          value={config.description}
          onChange={(e) => setConfig({ ...config, description: e.target.value })}
          placeholder="Brief description of what this does..."
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
        />
      </div>

      <button
        onClick={generateSnippet}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
      >
        <Code size={18} />
        Generate Snippet
      </button>

      {output && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Generated Code</label>
          <pre className="w-full bg-gray-800 border border-gray-700 rounded-lg p-4 text-green-400 font-mono text-sm overflow-auto max-h-96">
            {output}
          </pre>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={copyToClipboard}
          disabled={!output}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy Code'}
        </button>
        <button
          onClick={() => { setConfig({ language: 'javascript', type: 'function', name: '', params: '', description: '' }); setOutput(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear
        </button>
      </div>
    </div>
  );
};

export default CodeSnippetGeneratorTool;
